import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hierarchyApi } from '../../services/enterpriseApi';
import Modal from '../../components/common/Modal';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import Tooltip from '../../components/common/Tooltip';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  ChevronDown,
  MapPin,
  Settings,
  Users,
  Layers,
  Search,
  Info,
} from 'lucide-react';

interface HierarchyNode {
  id: string;
  name: string;
  type: string;
  parent_id: string | null;
  path: string;
  level: number;
  config: any;
  spatial_boundary?: any;
  children?: HierarchyNode[];
  expanded?: boolean;
}

export default function HierarchyManager() {
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editParentId, setEditParentId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const [newNode, setNewNode] = useState({
    name: '',
    type: 'department',
    parent_id: null as string | null,
    config: {},
    spatial_boundary: null,
  });

  const { data: hierarchy, isLoading: hierarchyLoading } = useQuery({
    queryKey: ['hierarchy-tree'],
    queryFn: () => hierarchyApi.getTree(),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['hierarchy-stats'],
    queryFn: () => hierarchyApi.getStats(),
  });

  const { data: nodeDetails } = useQuery({
    queryKey: ['hierarchy-node', selectedNode?.id],
    queryFn: () => hierarchyApi.getNode(selectedNode!.id),
    enabled: !!selectedNode,
  });

  const createNodeMutation = useMutation({
    mutationFn: (node: any) => hierarchyApi.createNode(node),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      queryClient.invalidateQueries({ queryKey: ['hierarchy-stats'] });
      alert('Node created successfully');
      setShowCreateModal(false);
      setNewNode({
        name: '',
        type: 'department',
        parent_id: null,
        config: {},
        spatial_boundary: null,
      });
    },
  });

  const updateNodeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      hierarchyApi.updateNode(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      queryClient.invalidateQueries({ queryKey: ['hierarchy-node'] });
      alert('Node updated successfully');
      setShowEditModal(false);
    },
  });

  const deleteNodeMutation = useMutation({
    mutationFn: (id: string) => hierarchyApi.deleteNode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      queryClient.invalidateQueries({ queryKey: ['hierarchy-stats'] });
      alert('Node deleted successfully');
      setSelectedNode(null);
    },
  });

  const moveNodeMutation = useMutation({
    mutationFn: ({ nodeId, newParentId }: { nodeId: string; newParentId: string | null }) =>
      hierarchyApi.moveNode(nodeId, newParentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      alert('Node moved successfully');
    },
  });

  if (hierarchyLoading || statsLoading) {
    return <LoadingScreen message="Loading organizational hierarchy..." />;
  }

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderTreeNode = (node: HierarchyNode, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode?.id === node.id;

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
            isSelected
              ? 'bg-primary-100 dark:bg-primary-900 border border-primary-300 dark:border-primary-600'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent'
          }`}
          style={{ marginLeft: `${level * 24}px` }}
          onClick={() => setSelectedNode(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          ) : (
            <div className="w-6" />
          )}
          <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          <span className="font-medium text-gray-900 dark:text-white">{node.name}</span>
          <span className="text-xs text-gray-600 dark:text-gray-300 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
            {node.type}
          </span>
          {node.spatial_boundary && (
            <MapPin className="w-3 h-3 text-green-600 dark:text-green-400" />
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredHierarchy = hierarchy?.data?.tree
    ? searchQuery
      ? filterTree(hierarchy.data.tree, searchQuery.toLowerCase())
      : hierarchy.data.tree
    : [];

  const statCards = [
    {
      title: 'Total Nodes',
      value: stats?.data?.total_nodes || 0,
      icon: Building2,
      color: 'bg-primary-500',
      tooltip: 'Total number of organizational nodes across all hierarchy levels in your tenant.',
    },
    {
      title: 'Max Depth',
      value: stats?.data?.max_depth || 0,
      icon: Layers,
      color: 'bg-blue-500',
      tooltip: 'Maximum nesting depth of your organizational hierarchy. Supports up to 6 levels for complex structures.',
    },
    {
      title: 'Root Nodes',
      value: stats?.data?.root_nodes || 0,
      icon: Building2,
      color: 'bg-green-500',
      tooltip: 'Number of top-level nodes without parent nodes. These form the foundation of your organizational structure.',
    },
    {
      title: 'With Boundaries',
      value: stats?.data?.nodes_with_boundaries || 0,
      icon: MapPin,
      color: 'bg-purple-500',
      tooltip: 'Nodes with defined spatial boundaries. These act as spatial containers for mission assignment and telemetry analysis.',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white flex items-center gap-2">
            Organizational Hierarchy
            <Tooltip content="Define custom multi-level organizational structures (e.g., Division > Estate > Garden > Plot for agriculture, or Project > Section > Sector for infrastructure). Each node can have spatial boundaries and inherit configurations from parent nodes." position="bottom">
              <Info className="w-5 h-5 text-gray-400 cursor-help" />
            </Tooltip>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage your organizational structure and spatial boundaries
          </p>
        </div>
        <Tooltip content="Create a new organizational node. Nodes can be nested up to 6 levels deep and support spatial containers for mission assignment.">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Node
          </Button>
        </Tooltip>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    {stat.title}
                    <Tooltip content={stat.tooltip} position="bottom">
                      <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                    </Tooltip>
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tree View */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              Hierarchy Tree
              <Tooltip content="Visual representation of your organizational structure. Click nodes to view details, use chevrons to expand/collapse branches. Nodes with spatial boundaries show a map pin icon.">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search nodes..."
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (expandedNodes.size > 0) {
                    setExpandedNodes(new Set());
                  } else {
                    const allIds = new Set<string>();
                    const collectIds = (nodes: HierarchyNode[]) => {
                      nodes.forEach((node) => {
                        allIds.add(node.id);
                        if (node.children) collectIds(node.children);
                      });
                    };
                    if (hierarchy?.data?.tree) collectIds(hierarchy.data.tree);
                    setExpandedNodes(allIds);
                  }
                }}
              >
                {expandedNodes.size > 0 ? 'Collapse All' : 'Expand All'}
              </Button>
            </div>
          </div>

          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filteredHierarchy.length > 0 ? (
              filteredHierarchy.map((node: HierarchyNode) => renderTreeNode(node))
            ) : (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">
                  {searchQuery ? 'No nodes found' : 'No organizational nodes'}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                    Create Your First Node
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Node Details */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            Node Details
            <Tooltip content="Detailed information about the selected node including its configuration, spatial boundaries, and child nodes. Each node inherits configurations from its parent unless explicitly overridden.">
              <Info className="w-4 h-4 text-gray-400 cursor-help" />
            </Tooltip>
          </h2>
          {selectedNode ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Name</label>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedNode.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">Type</label>
                <p className="text-gray-900 dark:text-white capitalize">{selectedNode.type}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Level</label>
                <p className="text-gray-900 dark:text-white">{selectedNode.level}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Path</label>
                <p className="text-sm text-gray-700 dark:text-gray-300 font-mono break-all">{selectedNode.path}</p>
              </div>

              {selectedNode.spatial_boundary && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Spatial Boundary
                  </label>
                  <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Type: {selectedNode.spatial_boundary.type}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Coordinates: {selectedNode.spatial_boundary.coordinates?.length || 0} points
                    </p>
                  </div>
                </div>
              )}

              {selectedNode.config && Object.keys(selectedNode.config).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <Settings className="w-4 h-4" />
                    Configuration
                  </label>
                  <pre className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs overflow-x-auto text-gray-900 dark:text-gray-100">
                    {JSON.stringify(selectedNode.config, null, 2)}
                  </pre>
                </div>
              )}

              {nodeDetails?.data?.children_count !== undefined && (
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Children
                  </label>
                  <p className="text-gray-900 dark:text-white">{nodeDetails.data.children_count} nodes</p>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditParentId(selectedNode.parent_id);
                    setShowEditModal(true);
                  }}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this node?')) {
                      deleteNodeMutation.mutate(selectedNode.id);
                    }
                  }}
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">Select a node to view details</p>
            </div>
          )}
        </Card>
      </div>

      {/* Create Node Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)} raw>
          <Card className="max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Create Node</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  Node Name
                  <Tooltip content="Descriptive name for this organizational node (e.g., 'North Division', 'Tea Garden A', 'Pipeline Sector 5').">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                <input
                  type="text"
                  value={newNode.name}
                  onChange={(e) => setNewNode({ ...newNode, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Engineering Department"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  Node Type
                  <Tooltip content="Configurable node labels to represent your organizational structure. Types can be customized per tenant (e.g., Division > Estate > Garden for agriculture, or Project > Section > Sector for infrastructure).">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                <select
                  value={newNode.type}
                  onChange={(e) => setNewNode({ ...newNode, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="division">Division</option>
                  <option value="department">Department</option>
                  <option value="team">Team</option>
                  <option value="region">Region</option>
                  <option value="site">Site</option>
                  <option value="estate">Estate</option>
                  <option value="garden">Garden</option>
                  <option value="plot">Plot</option>
                  <option value="project">Project</option>
                  <option value="section">Section</option>
                  <option value="sector">Sector</option>
                  <option value="facility">Facility</option>
                  <option value="zone">Zone</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  Parent Node (optional)
                  <Tooltip content="Select a parent node to create a nested structure. Child nodes inherit configurations (timezone, currency, data residency) from parents unless explicitly overridden. Leave empty to create a root node.">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                <select
                  value={newNode.parent_id || ''}
                  onChange={(e) =>
                    setNewNode({ ...newNode, parent_id: e.target.value || null })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">None (Root Node)</option>
                  {hierarchy?.data?.tree &&
                    flattenTree(hierarchy.data.tree).map((node: HierarchyNode) => (
                      <option key={node.id} value={node.id}>
                        {'  '.repeat(node.level)}{node.name} ({node.type})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  Configuration (JSON)
                  <Tooltip content="Custom configuration for this node in JSON format. Can include timezone, currency, measurement system, coordinate system, and functional scope bindings. Inherited from parent if not specified.">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                <textarea
                  value={JSON.stringify(newNode.config, null, 2)}
                  onChange={(e) => {
                    try {
                      const config = JSON.parse(e.target.value);
                      setNewNode({ ...newNode, config });
                    } catch (error) {
                      // Invalid JSON, ignore
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={6}
                  placeholder='{\n  "key": "value"\n}'
                />
              </div>

              <Alert type="info">
                Spatial boundaries can be added after creating the node
              </Alert>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => createNodeMutation.mutate(newNode)}
                  disabled={!newNode.name || createNodeMutation.isPending}
                >
                  {createNodeMutation.isPending ? 'Creating...' : 'Create Node'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}

      {/* Edit Node Modal */}
      {showEditModal && selectedNode && (
        <Modal onClose={() => setShowEditModal(false)} raw>
          <Card className="max-w-2xl w-full max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Edit Node: {selectedNode.name}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Node Name
                </label>
                <input
                  type="text"
                  defaultValue={selectedNode.name}
                  id="edit-name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  Parent Node
                  <Tooltip content="Move this node under a different parent to reorganise the hierarchy. Select 'None' to make it a root node." position="bottom">
                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  </Tooltip>
                </label>
                <select
                  value={editParentId || ''}
                  onChange={(e) => setEditParentId(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">None (Root Node)</option>
                  {hierarchy?.data?.tree &&
                    flattenTree(hierarchy.data.tree)
                      .filter((n: HierarchyNode) => n.id !== selectedNode.id)
                      .map((n: HierarchyNode) => (
                        <option key={n.id} value={n.id}>
                          {'  '.repeat(n.level)}{n.name} ({n.type})
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Configuration (JSON)
                </label>
                <textarea
                  defaultValue={JSON.stringify(selectedNode.config, null, 2)}
                  id="edit-config"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={8}
                />
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => {
                    const name = (document.getElementById('edit-name') as HTMLInputElement)
                      .value;
                    const configText = (
                      document.getElementById('edit-config') as HTMLTextAreaElement
                    ).value;
                    try {
                      const config = JSON.parse(configText);
                      updateNodeMutation.mutate({
                        id: selectedNode.id,
                        data: { name, config },
                      });
                      if (editParentId !== selectedNode.parent_id) {
                        moveNodeMutation.mutate({
                          nodeId: selectedNode.id,
                          newParentId: editParentId,
                        });
                      }
                    } catch (error) {
                      alert('Invalid JSON configuration');
                    }
                  }}
                  disabled={updateNodeMutation.isPending || moveNodeMutation.isPending}
                >
                  {updateNodeMutation.isPending || moveNodeMutation.isPending ? 'Updating...' : 'Update Node'}
                </Button>
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
}

// Helper function to filter tree
function filterTree(nodes: HierarchyNode[], query: string): HierarchyNode[] {
  return nodes.reduce((acc: HierarchyNode[], node) => {
    const matches = node.name.toLowerCase().includes(query);
    const filteredChildren = node.children ? filterTree(node.children, query) : [];

    if (matches || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children,
      });
    }

    return acc;
  }, []);
}

// Helper function to flatten tree
function flattenTree(nodes: HierarchyNode[]): HierarchyNode[] {
  return nodes.reduce((acc: HierarchyNode[], node) => {
    acc.push(node);
    if (node.children) {
      acc.push(...flattenTree(node.children));
    }
    return acc;
  }, []);
}

// Made with Bob
